// src/oura/components/aeo/InstrumentBadge.tsx
//
// Which instrument produced the visibility number (#3564, BE #4610).
//
// Oura used to ask the OpenAI API; it now asks the real ChatGPT app. The two
// disagree badly — 18/50 against 33/50 for the same business in the same week,
// because the API reads a general web crawl while the app answers local
// questions from a Maps place-card. A client checks the app, so the label is
// the difference between a figure they can reproduce and one they cannot.
//
// Three states this must never blur:
//
//   • measured in the app        — say so plainly
//   • fell back to the API       — say so LOUDLY. The number means something
//                                  different and a silent fallback is what
//                                  quietly loses trust
//   • partial                    — some questions reached the app, the rest are
//                                  API answers. Not a clean ChatGPT figure and
//                                  must not be presented as one

import { AlertTriangleIcon, CameraIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import type { AeoGrounding } from "@service/aeo";

/** A label ending in ", partial" is a mixed measurement. */
export function isPartialInstrument(g: AeoGrounding | null | undefined): boolean {
    return !!g?.instrumentLabel?.trim().toLowerCase().endsWith(", partial");
}

/** The run fell back to the API. `instrumentNote` carries the backend's reason. */
export function isFallbackInstrument(g: AeoGrounding | null | undefined): boolean {
    return g?.instrument === "openai_api" || !!g?.instrumentNote;
}

/** The run used both instruments — the two middle `instrumentLabel` cases. */
export function isMixedInstrument(g: AeoGrounding | null | undefined): boolean {
    return g?.mixed === true || isPartialInstrument(g);
}

/**
 * The engine name for the ChatGPT COLUMN header, read off the run rather than
 * off the engine key (#3616).
 *
 * On a successful browser scan the store's `openai` rows hold the ChatGPT app's
 * own text — that is how `analyze` scores them — so "ChatGPT" is correct. On a
 * run where the browser could not start, the same rows hold a genuine OpenAI
 * API answer, and the two indexes disagree badly (18/50 against 33/50 for the
 * same business in the same week). A column still headed CHATGPT then makes a
 * claim to the client that the backend refuses to make anywhere else, and a
 * client who checks in the app and finds the opposite stops believing the
 * report.
 *
 * So the header comes from `instrumentLabel`, trimmed to the engine name a
 * 96px column can hold: "ChatGPT (app), 44 of 50" reads "ChatGPT", and
 * "OpenAI (API), 38 of 50" reads "OpenAI". The full label and the backend's
 * `instrumentNote` belong in the tooltip beside it, verbatim.
 *
 * A scan from before this field existed keeps the old wording, which is what it
 * showed then — inventing a different one would be a worse guess.
 */
/**
 * The engine name a reader sees: "OpenAI (API), 38 of 50" -> "OpenAI",
 * "ChatGPT (app)" -> "ChatGPT", "ChatGPT: still being measured in the app
 * (12 of 50 asked so far)" -> "ChatGPT" (#4566: the status is `instrumentStatus`).
 *
 * The count and the instrument in brackets are detail, not identity. They are
 * still carried in full by `instrumentNote`, the note under the badge, and the
 * `title` on every element that shortens the string here, so nothing about how
 * the figure was measured is lost — it just stops being the loudest thing in a
 * name the client reads a dozen times on one screen.
 */
export function engineDisplayName(label: string): string {
    return label.split(",")[0].split("(")[0].split(":")[0].trim();
}

/**
 * The run's status the label carries after the engine name, for an info icon's
 * tooltip (#4566): "ChatGPT: still being measured in the app (12 of 50 asked so
 * far)" -> "still being measured in the app". null on a finished run
 * ("ChatGPT (app)"), so the header shows the name alone, as before.
 */
export function instrumentStatus(label: string | null | undefined): string | null {
    const text = label?.trim() ?? "";
    const at = text.indexOf(":");
    if (at < 0) return null;
    const status = text.slice(at + 1).split("(")[0].trim();
    return status || null;
}

export function instrumentColumnLabel(
    g: AeoGrounding | null | undefined,
    fallback: string,
): string {
    const label = g?.instrumentLabel?.trim();
    if (!label) return fallback;
    return engineDisplayName(label) || fallback;
}

/**
 * Whether the citation metrics can be shown at all.
 *
 * Absent means yes — that is every scan from before this field existed, and
 * they were shown then. Only an explicit `false` hides them, and hiding is the
 * point: some answers carry no citations at all, so a rendered 0% would be a
 * false claim about the client's own site rather than a measurement.
 */
export function citationsMeasurable(g: AeoGrounding | null | undefined): boolean {
    return g?.citationsMeasurable !== false;
}

const NOTE_SUMMARY_CHARS = 160;

/**
 * The first whole sentences of the note, up to about a line and a half.
 *
 * `instrumentNote` is written for the Visibility tab, which is a full-width
 * card. On this run it is 883 characters and ends in a raw `RuntimeError` with
 * the scraped sign-in page inside it, so rendered whole inside a gauge card it
 * pushed that card — and the two beside it — to three screens tall.
 *
 * Whole sentences where they fit, and a word-boundary cut where even the first
 * one does not — a single unpunctuated dump is the shape that broke the card,
 * so it cannot be the one case that escapes the budget. Never a mid-word cut.
 * The full text stays one hover away.
 */
export function instrumentNoteSummary(note: string, max: number = NOTE_SUMMARY_CHARS): string {
    const text = note.trim().replace(/\s+/g, " ");
    if (text.length <= max) return text;
    const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) ?? [];
    let out = "";
    for (const sentence of sentences) {
        if ((out + sentence).trim().length > max) break;
        out += sentence;
    }
    out = out.trim();
    // Not even the first sentence fits — which is the shape that broke the card,
    // a single unpunctuated dump. Cut on a word instead, never mid-word.
    if (!out) out = text.slice(0, max).replace(/\s+\S*$/, "");
    return out.length < text.length ? `${out}\u2026` : out;
}

export function InstrumentBadge({
    grounding,
    className,
    note = "full",
}: {
    grounding: AeoGrounding | null | undefined;
    className?: string;
    /**
     * `"full"` — the whole note, for a full-width card (the Visibility tab).
     * `"summary"` — the first sentences only, with the rest on hover, for a
     * narrow card that a long note would otherwise stretch.
     * `"none"` — the pill alone, for a caller that shows the note itself. The
     * Overview lifts it into a titled alert above the cards, so the reader is
     * told WHICH gauge the warning is about instead of reading a paragraph
     * wedged beside a ring.
     */
    note?: "full" | "summary" | "none";
}) {
    // A scan from before this shipped says nothing about how it was measured,
    // which is exactly what it did before. Better than inventing a label.
    if (!grounding?.instrumentLabel) return null;

    const fallback = isFallbackInstrument(grounding);
    const partial = isPartialInstrument(grounding);
    const flagged = fallback || partial;

    return (
        <div className={cn("flex flex-wrap items-start gap-2", className)}>
            <span
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
                    flagged
                        ? "border-amber-200 bg-amber-50 text-amber-800"
                        : "border-emerald-200 bg-emerald-50 text-emerald-800",
                )}
            >
                {flagged ? (
                    <AlertTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                ) : (
                    <CameraIcon className="h-3.5 w-3.5 flex-shrink-0" />
                )}
                {/* The engine name only. The backend still owns the string and
                    it is kept whole in the `title` and, when it matters, in the
                    note below — but the instrument in brackets is already
                    spelled out there in a full sentence, so repeating it inside
                    the name made the badge read as a caveat rather than a
                    label. */}
                <span title={grounding.instrumentLabel}>
                    {engineDisplayName(grounding.instrumentLabel)}
                </span>
            </span>

            {/* The fallback reason. A number produced by a different instrument
                than the one the client can check is the single finding they most
                need told to them — so it is shortened on a narrow card, never
                dropped, and the whole thing stays in the title. */}
            {grounding.instrumentNote && note !== "none" && (
                <span
                    className="text-xs leading-relaxed text-amber-800"
                    title={note === "summary" ? grounding.instrumentNote : undefined}
                >
                    {note === "summary"
                        ? instrumentNoteSummary(grounding.instrumentNote)
                        : grounding.instrumentNote}
                </span>
            )}

            {partial && !grounding.instrumentNote && note !== "none" && (
                <span className="text-xs leading-relaxed text-amber-800">
                    Only some questions were asked in the app; the rest are API answers.
                </span>
            )}
        </div>
    );
}
